import type { JobTemplateExportResult, JobTemplateImportResult } from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';
import { creationRefusal } from './job-creation';

export interface JobSetupFileOptions {
  /** The API's root (`/v1`); the import makes a Paige job, so it is the API's, never a job's channel. */
  apiOrigin?: string;
  tenant?: string;
}

/**
 * CapturePoint's Download job setup and Import job setup, in the browser. The desktop's dialogs
 * wrote the file to a path and read one from a path; here the job setup file is downloaded as the
 * browser saves any file, and an imported one goes up as text to `POST /v1/jobs/setup-imports`,
 * which imports it in the engine as a new job and makes the Paige job bound to it. The new job then
 * opens on its own route, where the desktop listed it in Scan jobs (which Paige does not have).
 */
export function jobSetupFiles(
  call: EngineCall,
  { apiOrigin = '/v1', tenant }: JobSetupFileOptions,
  bindChannel: (workspaceJobId: string, paigeJobId: string) => void,
): Record<string, unknown> {
  return {
    exportJobTemplate: async (request: { jobId: string; jobName: string }) => {
      const download = await call<{ file: string; result: JobTemplateExportResult }>(
        'export-job-template',
        { jobId: request.jobId },
      );
      saveFile(`${fileNameFor(request.jobName)}.json`, download.file);
      return download.result;
    },
    importJobTemplate: async (): Promise<JobTemplateImportResult | null> => {
      const file = await pickSetupFile();
      if (!file) return null;
      const imported = await importSetup(apiOrigin, tenant, await file.text());
      if (imported.job.workspaceJobId) bindChannel(imported.job.workspaceJobId, imported.job.id);
      window.paigeWorkbench?.created?.(imported.job.id);
      return imported.setup;
    },
    // CapturePoint's setup file waiting for a reading slot on the desktop (CPv6 8f4396e6): Paige's import is
    // one request to the API, with no local reading to wait for, so no such work is ever reported.
    onJobSetupFileWorking: () => () => undefined,
    // Import job setup lists the unfinished imports of the earlier application on this computer's
    // disk when it opens (legacy-import); Paige holds none.
    listLegacyImports: async () => [],
  };
}

/** What the import answers: the Paige job, and the engine's account of what came with it. */
interface ImportedSetup {
  job: { id: string; workspaceJobId?: string | null };
  setup: JobTemplateImportResult;
}

async function importSetup(
  apiOrigin: string,
  tenant: string | undefined,
  text: string,
): Promise<ImportedSetup> {
  const response = await fetch(`${apiOrigin}/jobs/setup-imports`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'Idempotency-Key': crypto.randomUUID(),
      ...(tenant ? { 'Paige-Tenant': tenant } : {}),
    },
    body: text,
  });
  if (!response.ok) throw new Error(await creationRefusal(response));
  return (await response.json()) as ImportedSetup;
}

/** The browser's file picker for one job setup file; null when the person closes it. */
function pickSetupFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.style.display = 'none';
    const done = (file: File | null) => {
      resolve(file);
      input.remove();
    };
    input.onchange = () => done(input.files?.[0] ?? null);
    input.oncancel = () => done(null);
    document.body.appendChild(input);
    input.click();
  });
}

/** A job's name as a file name: what a file system would refuse becomes a dash. */
export function fileNameFor(jobName: string): string {
  const safe = jobName.replace(/[\\/:*?"<>|]+/g, '-').trim();
  return safe || 'job-setup';
}

function saveFile(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
